import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Dimensions, PanResponder, Platform, StyleSheet, View, TouchableOpacity } from 'react-native';
import MyAppText from './MyAppText';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface IButtonFloatingAction {
    name: string;
    label?: string;
    icon?: IconProp;
    color?: string;
    disabled?: boolean;
    onPress: () => void;
}

interface IButtonFloatingActionOverride extends IButtonFloatingAction {
    icon: IconProp;
}

interface Offset {
    vertical?: number;
    horizontal?: number;
}

type Props =
    | {
        overrideWithAction: true;
        actions: [IButtonFloatingActionOverride];
        visible?: boolean;
        offset?: Offset;
        draggable?: boolean;
    }
    | {
        overrideWithAction?: false;
        actions: IButtonFloatingAction[];
        visible?: boolean;
        offset?: Offset;
        draggable?: boolean;
    };

const BUTTON_DIAMETER = 64;
const DRAG_ACTIVATION_THRESHOLD = 6;
const OVERLAY_COLOR = 'rgba(68, 68, 68, 0.6)';

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
}

export default function FloatingButton({ actions, offset, overrideWithAction, visible = true, draggable = false }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const { width, height } = Dimensions.get('window');

    const rotation = useRef(new Animated.Value(0)).current;
    const fabScale = useRef(new Animated.Value(0)).current;
    const menuAnim = useRef(new Animated.Value(0)).current;

    const baseOffset = useMemo(() => ({
        vertical: offset?.vertical ?? 26,
        horizontal: offset?.horizontal ?? 32,
    }), [offset?.vertical, offset?.horizontal]);

    const [dragOffset, setDragOffset] = useState({ dx: 0, dy: 0 });
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const dragOffsetRef = useRef({ dx: 0, dy: 0 });
    const dragStart = useRef({ dx: 0, dy: 0 });
    const isDragging = useRef(false);

    useEffect(() => {
        if (visible) {
            fabScale.setValue(0);
            Animated.spring(fabScale, {
                toValue: 1,
                friction: 5,
                tension: 40,
                useNativeDriver: true,
            }).start();
        } else {
            fabScale.setValue(0);
        }
    }, [visible]);

    const currentX = width - baseOffset.horizontal - BUTTON_DIAMETER + dragOffset.dx;
    const currentY = height - baseOffset.vertical - BUTTON_DIAMETER + dragOffset.dy;
    const centerX = currentX + BUTTON_DIAMETER / 2;
    const centerY = currentY + BUTTON_DIAMETER / 2;

    const isLeft = centerX < width / 2;
    const isTop = centerY < height / 2;

    function updateOffset(next: { dx: number; dy: number; }) {
        dragOffsetRef.current = next;
        setDragOffset(next);
    }

    const panResponder = useMemo(() => PanResponder.create({
        onStartShouldSetPanResponderCapture: () => false,
        onMoveShouldSetPanResponderCapture: (_evt, gestureState) => {
            if (!draggable || isMenuVisible) {
                return false;
            }
            return Math.abs(gestureState.dx) > DRAG_ACTIVATION_THRESHOLD || Math.abs(gestureState.dy) > DRAG_ACTIVATION_THRESHOLD;
        },
        onPanResponderGrant: () => {
            isDragging.current = false;
            dragStart.current = dragOffsetRef.current;
        },
        onPanResponderMove: (_evt, gestureState) => {
            if (Math.abs(gestureState.dx) > DRAG_ACTIVATION_THRESHOLD || Math.abs(gestureState.dy) > DRAG_ACTIVATION_THRESHOLD) {
                isDragging.current = true;
            }
            const minDx = -(width - BUTTON_DIAMETER - baseOffset.horizontal);
            const maxDx = baseOffset.horizontal;
            const minDy = -(height - BUTTON_DIAMETER - baseOffset.vertical);
            const maxDy = baseOffset.vertical;

            updateOffset({
                dx: clamp(dragStart.current.dx + gestureState.dx, minDx, maxDx),
                dy: clamp(dragStart.current.dy + gestureState.dy, minDy, maxDy),
            });
        },
        onPanResponderRelease: () => {
            setTimeout(() => {
                isDragging.current = false;
            }, 50);
        },
        onPanResponderTerminate: () => {
            isDragging.current = false;
        },
        onPanResponderTerminationRequest: () => true,
    }), [draggable, baseOffset.vertical, baseOffset.horizontal, width, height, isMenuVisible]);

    const openMenu = () => {
        setIsMenuVisible(true);
        Animated.parallel([
            Animated.timing(rotation, {
                toValue: 1,
                duration: 250,
                useNativeDriver: true,
            }),
            Animated.spring(menuAnim, {
                toValue: 1,
                bounciness: 8,
                speed: 12,
                useNativeDriver: true,
            })
        ]).start();
    };

    const closeMenu = () => {
        Animated.parallel([
            Animated.timing(rotation, {
                toValue: 0,
                duration: 200,
                useNativeDriver: true,
            }),
            Animated.timing(menuAnim, {
                toValue: 0,
                duration: 180,
                useNativeDriver: true,
            })
        ]).start(() => setIsMenuVisible(false));
    };

    const toggleMenu = () => {
        if (isDragging.current) {
            return;
        }
        if (isMenuVisible) {
            closeMenu();
        } else {
            openMenu();
        }
    };

    const rotateInterpolate = rotation.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '45deg'],
    });

    function getIconColor(action: IButtonFloatingAction) {
        if (action.color) {
            return action.color;
        }
        if (UnistylesRuntime.themeName === 'dark') {
            return '#000000';
        }
        return theme.colors.primary['500'];
    }

    function getButtonBorderColor(action: IButtonFloatingAction) {
        if (action.disabled) {
            return theme.colors.neutral['200'];
        }
        if (action.color) {
            return action.color;
        }
        if (UnistylesRuntime.themeName === 'dark') {
            return theme.colors.white['500'];
        }
        return theme.colors.primary['500'];
    }

    const renderActionItem = (action: IButtonFloatingAction, index: number) => {
        const distanceMultiplier = isTop ? (index + 1) : (actions.length - index);
        const distance = distanceMultiplier * 68;

        const translateY = menuAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [0, isTop ? distance : -distance]
        });

        const opacity = menuAnim.interpolate({
            inputRange: [0, 0.2, 1],
            outputRange: [0, 0, 1]
        });

        const handlePress = () => {
            if (Platform.OS === 'web' && typeof document !== 'undefined') {
                (document.activeElement as HTMLElement | null)?.blur();
                document.body?.focus({ preventScroll: true });
            }
            if (action.onPress) {
                action.onPress();
            }
            closeMenu();
        };

        const Label = action.label && (
            <MyAppText style={[styles.itemText, styles.dropShadow]}>
                {action.label}
            </MyAppText>
        );

        const renderIcon = () => {
            if (overrideWithAction) {
                const overrideAction = action as IButtonFloatingActionOverride;
                return (
                    <View style={[styles.itemButton(action.disabled ? theme.colors.neutral['200'] : theme.colors.primary['500']), styles.dropShadow, { backgroundColor: action.disabled ? theme.colors.neutral['200'] : theme.colors.primary['500'] }]}>
                        <FontAwesomeIcon icon={overrideAction.icon} color={theme.colors.white['500']} fontSize={24} />
                    </View>
                );
            }
            if (action.icon) {
                return (
                    <View style={[styles.itemButton(getButtonBorderColor(action)), styles.dropShadow, { backgroundColor: action.disabled ? theme.colors.neutral['200'] : '#ffffff', }]}>
                        <FontAwesomeIcon icon={action.icon} color={getIconColor(action)} fontSize={24} />
                    </View>
                );
            }
            return null;
        };

        return (
            <Animated.View
                key={action.name || index.toString()}
                style={{
                    position: 'absolute',
                    top: 4,
                    width: 300,
                    [isLeft ? 'left' : 'right']: 4,
                    alignItems: isLeft ? 'flex-start' : 'flex-end',
                    transform: [{ translateY }],
                    opacity,
                }}
                pointerEvents={isMenuVisible ? 'auto' : 'none'}
            >
                <TouchableOpacity
                    activeOpacity={0.8}
                    onPress={handlePress}
                    disabled={!isMenuVisible || action.disabled}
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                    }}
                >
                    {isLeft ? (
                        <>
                            {renderIcon()}
                            {!overrideWithAction && Label}
                        </>
                    ) : (
                        <>
                            {!overrideWithAction && Label}
                            {renderIcon()}
                        </>
                    )}
                </TouchableOpacity>
            </Animated.View>
        );
    };

    if (!visible) {
        return null;
    }

    const customFloatingUI = (
        <View style={{
            position: 'absolute',
            bottom: baseOffset.vertical,
            right: baseOffset.horizontal,
            width: BUTTON_DIAMETER,
            height: BUTTON_DIAMETER,
        }}>
            {actions.map((action, index) => renderActionItem(action, index))}

            <Animated.View style={{ transform: [{ scale: fabScale }], width: '100%', height: '100%' }}>
                <TouchableOpacity
                    activeOpacity={0.8}
                    onPress={toggleMenu}
                    style={[styles.mainButton(theme.colors.primary['500']), styles.dropShadow]}
                >
                    <Animated.View style={{ transform: [{ rotate: rotateInterpolate }] }}>
                        <FontAwesomeIcon icon={faPlus} color={theme.colors.white['500']} fontSize={24} />
                    </Animated.View>
                </TouchableOpacity>
            </Animated.View>
        </View>
    );

    return (
        <>
            {isMenuVisible && (
                <Animated.View
                    style={[
                        StyleSheet.absoluteFillObject,
                        { backgroundColor: OVERLAY_COLOR, opacity: rotation }
                    ]}
                    onStartShouldSetResponder={() => true}
                    onResponderRelease={closeMenu}
                />
            )}

            {draggable ? (
                <View
                    style={[
                        StyleSheet.absoluteFillObject,
                        { transform: [{ translateX: dragOffset.dx }, { translateY: dragOffset.dy }] },
                    ]}
                    pointerEvents="box-none"
                    {...panResponder.panHandlers}
                >
                    {customFloatingUI}
                </View>
            ) : (
                <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
                    {customFloatingUI}
                </View>
            )}
        </>
    );
}

const styleSheet = createStyleSheet(() => ({
    itemText: {
        fontWeight: '600',
        fontSize: 14,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 4,
        backgroundColor: '#3C3C3D',
        color: '#ffffff',
        maxWidth: 220,
        flexShrink: 0
    },
    mainButton: (backgroundColor: string) => ({
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor,
        alignItems: 'center',
        justifyContent: 'center',
    }),
    itemButton: (borderColor: string) => ({
        width: 56,
        height: 56,
        borderRadius: 28,
        borderWidth: 2,
        borderColor,
        alignItems: 'center',
        justifyContent: 'center',
    }),
    dropShadow: {
        shadowColor: '#000000',
        shadowRadius: 4,
        shadowOpacity: 0.2,
        shadowOffset: {
            width: -1,
            height: 2
        }
    }
}));
